class DonationForm extends HTMLElement {
    constructor() {
      super();
    }
  
    async connectedCallback() {
      // Wait for the next frame to ensure DOM is ready
      await new Promise(resolve => requestAnimationFrame(resolve));
  
      // Get donation product data
      this.monthlySellingPlanId = null;
      this.productId = null;
      try {
        const monthlyPlanData = JSON.parse(this.querySelector('#monthly-plan-data').textContent);
        this.monthlySellingPlanId = monthlyPlanData.id;
        this.productId = monthlyPlanData.productId;
      } catch (e) {
        // Monthly plan not available
      }
  
      // Get elements
      this.customAmountContainer = this.querySelector('#custom-amount-container');
      this.customAmountInput = this.querySelector('#custom-amount');
      this.donateButton = this.querySelector('#donate-button');
      this.amountRadios = this.querySelectorAll('input[name="donation-amount"]');
      this.tabButtons = this.querySelectorAll('.single-tab');
      this.sellingPlanInput = this.querySelector('#selling-plan-input');
      this.customAmountMessage = this.querySelector('#custom-amount-message');
      this.donationMessage = this.querySelector('#donation-message');
      this.formError = this.querySelector('#donation-form-error');
  
      if (!this.customAmountContainer || !this.donateButton) {
        console.error('Required elements not found in donation form');
        return;
      }
  
      // Add event listeners
      this.setupEventListeners();
      this.checkCart(this.productId);
    }
  
    showError(message) {
      if (!this.formError) return;
      const messageEl = this.formError.querySelector('span') || this.formError;
      messageEl.textContent = message;
      this.formError.classList.add('is-visible');
      this.formError.setAttribute('aria-hidden', 'false');
      if (this.customAmountInput) {
        this.customAmountInput.setAttribute('aria-invalid', 'true');
      }
    }
  
    clearError() {
      if (!this.formError) return;
      this.formError.classList.remove('is-visible');
      this.formError.setAttribute('aria-hidden', 'true');
      if (this.customAmountInput) {
        this.customAmountInput.removeAttribute('aria-invalid');
      }
    }
  
    setupEventListeners() {
      // Handle tab switching
      this.tabButtons.forEach(tab => {
        tab.addEventListener('click', () => this.handleTabSwitch(tab));
      });
  
      // Handle amount selection
      this.amountRadios.forEach(radio => {
        radio.addEventListener('change', () => this.handleAmountChange(radio));
      });
  
      if (this.customAmountInput) {
        this.customAmountInput.addEventListener('input', () => this.clearError());
      }
  
      // Handle donate button click
      this.donateButton.addEventListener('click', () => this.handleDonateClick());
    }
  
    handleTabSwitch(tab) {
      this.tabButtons.forEach(t => t.classList.remove('selected'));
      tab.classList.add('selected');
  
      const donationType = tab.getAttribute('data-donation-type');
      if (donationType === 'monthly' && this.monthlySellingPlanId) {
        this.sellingPlanInput.value = this.monthlySellingPlanId;
      } else {
        this.sellingPlanInput.value = '';
      }
    }
  
    handleAmountChange(radio) {
      const message = radio.getAttribute('data-donation-message');
      const isCustom = radio.value === 'custom';
  
      this.clearError();
  
      if (isCustom) {
        this.donationMessage.style.display = 'none';
        this.customAmountMessage.textContent = message || '';
        this.customAmountContainer.style.display = 'block';
        this.customAmountInput.required = true;
        if (!this.customAmountInput.value) {
          this.customAmountInput.value = this.customAmountInput.min || '10';
        }
      } else {
        this.donationMessage.style.display = 'block';
        this.customAmountMessage.textContent = '';
        this.donationMessage.textContent = message || '';
        this.customAmountContainer.style.display = 'none';
        this.customAmountInput.required = false;
        this.customAmountInput.value = this.customAmountInput.min || '10';
      }
    }
  
    handleDonateClick() {
      const selectedAmount = this.querySelector('input[name="donation-amount"]:checked');
  
      this.clearError();
  
      if (!selectedAmount) {
        this.showError('Please select a donation amount');
        return;
      }
  
      let variantId = selectedAmount.getAttribute('data-variant-id');
      let quantity = 1;
  
      if (selectedAmount.value === 'custom') {
        const customAmount = parseInt(this.customAmountInput.value, 10);
        const minAmount = parseInt(this.customAmountInput.min, 10) || 10;
        if (!customAmount || customAmount < minAmount) {
          this.showError(`Please enter a custom amount of at least $${minAmount}`);
          this.customAmountInput.focus();
          return;
        }
        quantity = customAmount;
      }
  
      this.addToCartAndCheckout(variantId, quantity);
    }
  
    addToCartAndCheckout(variantId, quantity) {
      this.donateButton.textContent = 'Processing...';
      this.donateButton.disabled = true;
  
      const cartData = {
        id: variantId,
        quantity: quantity
      };
  
      if (this.sellingPlanInput.value) {
        cartData.selling_plan = this.sellingPlanInput.value;
      }
  
      fetch('/cart/clear.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      .then(response => {
        if (!response.ok) throw new Error('Failed to clear cart');
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(cartData)
        });
      })
      .then(response => {
        if (!response.ok) throw new Error('Failed to add item to cart');
        return response.json();
      })
      .then(() => {
        window.location.href = '/checkout';
      })
      .catch(error => {
        console.error('Error:', error);
        this.showError('There was an error processing your donation. Please try again.');
        this.donateButton.textContent = this.donateButton.getAttribute('data-default-text');
        this.donateButton.disabled = false;
      });
    }
  
    checkCart(productId) {
      if (!productId) return;
  
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          const donationItem = cart.items.find(item => item.product_id === productId);
  
          if (donationItem) {
            let amount;
            if (donationItem.variant_title) {
              amount = donationItem.variant_title.replace(/[^0-9]/g, '');
            } else {
              amount = donationItem.original_price / 100;
            }
  
            const amountRadio = this.querySelector(`input[data-variant-id="${donationItem.variant_id}"]`);
            if (amountRadio) {
              const isCustomAmount = donationItem.variant_id === amountRadio.getAttribute('data-variant-id') && donationItem.price === 100;
  
              if (isCustomAmount) {
                amountRadio.checked = true;
                amountRadio.dispatchEvent(new Event('change'));
                this.customAmountInput.value = amount;
              } else {
                amountRadio.checked = true;
                amountRadio.dispatchEvent(new Event('change'));
              }
            }
  
            if (donationItem.selling_plan_allocation) {
              const monthlyTab = this.querySelector('[data-donation-type="monthly"]');
              if (monthlyTab) {
                monthlyTab.click();
              }
            }
  
            if (donationItem.total_discount > 0 && donationItem.discount_codes && donationItem.discount_codes.length > 0) {
              this.donateButton.textContent = 'Update Donation';
            }
          }
        })
        .catch(error => console.error('Error fetching cart:', error));
    }
  }
  
  customElements.define('donation-form', DonationForm);